import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useIntl } from "react-intl";

import Card from "../../UI/Card/Card";
import ErrorPanel, { ErrorSize } from "../../UI/ErrorPanel/ErrorPanel";
import Button from "../../UI/Button/Button";
import LinkButton from "../../UI/Button/LinkButton";
import { faCheck, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import * as ApplicationUtils from "../../Utils/ApplicationsUtils";
import * as ApplicationsActions from "../../../middleware/Store/Actions/ApplicationsActions";
import * as SandBoxApplicationsActions from "../../../middleware/Store/Actions/SandBoxApplicationsActions";
import * as ContactUtils from "../../Utils/ContactUtils";
import * as ErrorUtils from "../../Utils/ErrorUtils";
import * as ApplicationConstant from "../../Constants/ApplicationConstant";
import CheckBox from '../../UI/Button/CheckboxButton';

import CountriesList from "../../../middleware/Constants/CountriesList";
import UsStatesList from "../../../middleware/Constants/USStatesList";
import CanStatesList from "../../../middleware/Constants/CANStatesList";
import * as CountriesUtils from "../../../middleware/Utils/CountriesUtils";

import LogService from "../../../middleware/CommonSdk/LogService";
const LOG_TAG = "SETTINGS-REGISTER";

const RegisterDeveloper = (props) => {

    const intl = useIntl();
    const [companyName, setCompanyName] = useState("");
    const [country, setCountry] = useState("");
    const [countryState, setCountryState] = useState("");
    const [isAgreed, setIsAgreed] = useState(false);
    const [progress, setProgress] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (props.user) {
            if (!ContactUtils.isInDefaultCompany(props.user) && props.user.companyName) {
                setCompanyName(props.user.companyName);
            }
            setCountry(props.user.country);
            setCountryState(props.user.state || "");
        }

    }, [props.user]);

    const handleRegister = (event) => {
        LogService.trace(LOG_TAG, "handleRegister", "Register as developper");
        setProgress(true);
        let company = null;
        if (ContactUtils.isInDefaultCompany(props.user)) {
            company = {
                companyName: companyName,
                country: country,
                state: countryState
            }
        }
        props.registerDeveloper(company)
            .then(() => {
                LogService.debug(LOG_TAG, "handleRegister success");
                setProgress(false);
                setIsAgreed(false);
            })
            .catch((errorPayload) => {
                LogService.debug(LOG_TAG, "handleRegister error");
                setProgress(false);
                setIsAgreed(false);
                setErrorMessage(ErrorUtils.getPrettyErrorMessage(errorPayload, intl));
            });
    }

    const handleUnregister = (event) => {
        LogService.trace(LOG_TAG, "handleUnregister", "Unegister as developper");
        setProgress(true);
        props.unregisterDeveloper(props.user?.loginEmail)
            .then(() => {
                LogService.debug(LOG_TAG, "handleUnregister success");
                setProgress(false);
                setErrorMessage("");
            })
            .catch((errorPayload) => {
                LogService.debug(LOG_TAG, "handleUnregister error");
                setProgress(false);
                setErrorMessage(ErrorUtils.getPrettyErrorMessage(errorPayload, intl));
            });
    }

    const handleAgreement = (isChecked) => {
        LogService.trace(LOG_TAG, "handleAgreement");
        setIsAgreed(isChecked);
    }

    const handleCompanyNameChange = (event) => {
        setCompanyName(event.target.value);
    }

    const handleCountryChange = (event) => {
        LogService.trace(LOG_TAG, "handleCountryChange");
        setCountry(event.target.value);
    }

    const handleStateChange = (event) => {
        LogService.trace(LOG_TAG, "handleStateChange");
        setCountryState(event.target.value);
    }

    LogService.trace(LOG_TAG, "render");
    const count = ApplicationUtils.myApplications(props.applications, props.user).length;
    const isDeveloper = ContactUtils.isDeveloper(props.user);

    let cardTitle = "";
    let registerForm = null;
    let button = null;
    if (isDeveloper) {//unregister
        cardTitle = intl.messages.settingsUnregisterTitle;
        registerForm =
            <div id="unregisterForm" className="panel bg-fog mb-3">
                {intl.formatMessage({ id: "settingsUnregisterMessage" }, { b: chunks => <b>{chunks}</b> })}
                {count > 0 &&
                    <div className="mt-3">
                        <span className="d-block ">
                            {intl.formatMessage({ id: "settingsUnregisterStillHaveApplicationsMessage" }, { count: count, b: chunks => <b>{chunks}</b> })}
                        </span>
                        <span>
                            <LinkButton
                                className="btn btn-link"
                                icon={faArrowRight}
                                label={intl.messages.gotoApplicationPage}
                                to={props.isSandBox ? ApplicationConstant.SANDBOX_PATH : ApplicationConstant.APPLICATIONS_PATH}
                            />
                        </span>
                    </div>
                }
            </div>

        const tooltipTitle = count > 0 ? intl.messages.settingsUnregisterTooltipMessage : "";
        button =
            <Button
                label={intl.messages.unregister}
                icon={faCheck}
                className="btn-submit"
                spinner={progress}
                disabled={count > 0}
                tooltipTitle={tooltipTitle}
                onClick={handleUnregister}
            />
    } else {//register
        const countriesOptions = [
            <option value="" key="0" disabled>{intl.messages.settingsRegisterCountryPlaceholder}</option>,
            ...CountriesList.map(country => {
                return (
                    <option value={country["alpha-3"]} key={country["alpha-3"]}>
                        {country.name}
                    </option>
                );
            })
        ];

        let statesOptions = null;
        if (CountriesUtils.isCanadianOrUsaCountry(country)) {
            statesOptions = [<option value="" key="0" disabled>{intl.messages.settingsRegisterStatePlaceholder}</option>];
            if (CountriesUtils.isUsaCountry(country)) {
                statesOptions = [
                    ...statesOptions,
                    ...UsStatesList.map(state => {
                        return (
                            <option value={state.abbreviation} key={state.abbreviation}>
                                {state.name}
                            </option>
                        );
                    })
                ];
            } else if (CountriesUtils.isCanadianCountry(country)) {
                statesOptions = [
                    ...statesOptions,
                    ...CanStatesList.map(state => {
                        return (
                            <option value={state.abbreviation} key={state.abbreviation}>
                                {state.name}
                            </option>
                        );
                    })
                ];
            }
        }

        const isInDefaultCompany = ContactUtils.isInDefaultCompany(props.user);

        cardTitle = intl.messages.settingsRegisterTitle;
        let enableRegisterButton = isAgreed && country?.length > 0;
        if (isInDefaultCompany && (props.user.companyName.toLowerCase() === companyName.trim().toLowerCase() || companyName.length === 0)) {
            enableRegisterButton = false;
        }
        if (CountriesUtils.isCanadianOrUsaCountry(country) && countryState.length === 0) {
            enableRegisterButton = false;
        }

        let companyMsg = isInDefaultCompany ? intl.messages.settingsRegisterDefaultCompanyMessage : intl.messages.settingsRegisterCompanyMessage;
        let linkAgreement = intl.formatMessage({ id: "settingsRegisterAgrementButtonTitle" }, {
            link: <a
                target="_blank"
                rel="noopener noreferrer"
                href="./legals/Rainbow_API_HUB_Developer_Agreement.pdf"
            >
                {intl.messages.settingsRegisterAgrementButtonLinkTitle}
            </a>
        });

        registerForm =
            <form className="form-group">
                <div id="registerMessageId" className="panel bg-fog mb-3">
                    {intl.formatMessage({ id: "settingsRegisterMessage" }, { b: chunks => <b>{chunks}</b> })}
                </div>
                <div id="companyAndCountryId">
                    <p>{companyMsg}</p>
                    <div id="companyId" className="form-group">
                        <label className="input-label">{intl.messages.settingsRegisterCompanyNameLabel}</label>
                        <input
                            value={companyName}
                            type="text"
                            className="form-control"
                            autoComplete="off"
                            placeholder={intl.messages.settingsRegisterCompanyNamePlaceholder}
                            onChange={handleCompanyNameChange}
                            disabled={!isInDefaultCompany}
                        />
                    </div>
                    <div id="countryId" className="form-group">
                        <label className="input-label">{intl.messages.settingsRegisterCountryLabel}</label>
                        <select
                            className="form-control"
                            value={country}
                            onChange={handleCountryChange}
                            disabled={!isInDefaultCompany}
                        >
                            {countriesOptions}
                        </select>
                    </div>
                    {CountriesUtils.isCanadianOrUsaCountry(country) && (
                        <div id="stateId" className="form-group">
                            <label className="input-label">{intl.messages.settingsRegisterStateLabel}</label>
                            <select
                                className="form-control"
                                value={countryState}
                                onChange={handleStateChange}
                                disabled={!isInDefaultCompany}
                            >
                                {statesOptions}
                            </select>
                        </div>
                    )}
                </div>
                <div id="agreementId">
                    <label className="d-block panelLabel">
                        {intl.messages.settingsRegisterAgrementMessage}
                    </label>
                    <CheckBox
                        classes="d-inline-block"
                        checked={isAgreed}
                        onClick={handleAgreement}
                        labelArray={linkAgreement}
                    />
                </div>
            </form >
        button =
            <Button
                label={intl.messages.settingsRegisterButton}
                icon={faCheck}
                onClick={handleRegister}
                spinner={progress}
                disabled={!enableRegisterButton}
            />
    }

    return (
        <Card title={cardTitle}>
            {registerForm}
            {button}
            {(errorMessage) &&
                <ErrorPanel id="option-error" size={ErrorSize.Small}>{errorMessage}</ErrorPanel>
            }
        </Card>
    );
}

RegisterDeveloper.defaultProps = {
    isSandBox: false
}

RegisterDeveloper.propTypes = {
    user: PropTypes.object,
    applications: PropTypes.array,
    error: PropTypes.object,
    progress: PropTypes.bool,
    isSandBox: PropTypes.bool
}

const mapStateToProps = (state, props) => {
    return {
        user: props.isSandBox ? state.sandBoxContacts.user : state.contacts.user,
        applications: props.isSandBox ? state.sandBoxApplications.applications : state.applications.applications
    };
};

const mapDispatchToProps = (dispatch, props) => {
    return {
        registerDeveloper: (company) => dispatch(props.isSandBox ? SandBoxApplicationsActions.registerDeveloper() : ApplicationsActions.registerDeveloper(company)),
        unregisterDeveloper: (email) => dispatch(props.isSandBox ? SandBoxApplicationsActions.unregisterDeveloper(email) : ApplicationsActions.unregisterDeveloper(email))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(RegisterDeveloper);